import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { Get, Post, Put, DeleteSingle } from '@app/utils/httpProvider';
import { toast } from 'react-toastify';
import Table from 'react-bootstrap/Table';
import Pagination from 'react-js-pagination';
import { Modal, Button, Form } from 'react-bootstrap';
import { ConfirmModal } from '../../modules/modal/ConfirmModal';
import { messageToast, objectShowNumber } from '../../utils/define';

const CategoryManager = ({ api, title, entityName, codeField, nameField }) => {
    const valueSearch = useSelector((state) => state.search.valueSearch);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [formData, setFormData] = useState({ code: '', name: '', description: '' });

    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    useEffect(() => {
        setPage(1);
    }, [valueSearch]);

    const { data, isLoading, refetch } = useQuery(
        [entityName, page, pageSize, valueSearch],
        () => Get(api.GET_PAGING, null, { PageIndex: page, PageSize: pageSize, Keyword: valueSearch || '' }),
        { keepPreviousData: true }
    );

    const createMutation = useMutation((newItem) => Post(api.ADD_ITEM, null, newItem));
    const updateMutation = useMutation((item) => Put(api.UPDATE_ITEM, item.id, item));
    const deleteMutation = useMutation((id) => DeleteSingle(api.DELETE_ITEM, id));

    const handleOpenAdd = () => {
        setIsEdit(false);
        setFormData({ code: '', name: '', description: '' });
        setShowModal(true);
    };

    const handleOpenEdit = (item) => {
        setIsEdit(true);
        setCurrentItem(item);
        setFormData({
            code: item[codeField],
            name: item[nameField],
            description: item.description || ''
        });
        setShowModal(true);
    };

    const handleSave = () => {
        if (!formData.code || !formData.name) {
            toast.error("Vui lòng nhập đầy đủ Mã và Tên");
            return;
        }

        const payload = {
            [codeField]: formData.code,
            [nameField]: formData.name,
            description: formData.description
        };

        if (isEdit) {
            updateMutation.mutate({ ...payload, id: currentItem.id }, {
                onSuccess: () => {
                    toast.success("Cập nhật thành công");
                    setShowModal(false);
                    refetch();
                },
                onError: (err) => toast.error(err?.message || "Có lỗi xảy ra")
            });
        } else {
            createMutation.mutate(payload, {
                onSuccess: () => {
                    toast.success("Thêm mới thành công");
                    setShowModal(false);
                    refetch();
                },
                onError: (err) => toast.error(err?.message || "Có lỗi xảy ra")
            });
        }
    };

    const handleDelete = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name);
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        deleteMutation.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res.ok || res.status < 400) {
                    toast.success(messageToast.messageToastDeleteSuccess);
                    refetch();
                } else {
                    // Xử lý lỗi từ backend (ví dụ đang được sử dụng)
                    res.json().then(data => {
                        toast.error(data.message || messageToast.messageToastDeleteError);
                    }).catch(() => {
                        toast.error(messageToast.messageToastDeleteError);
                    });
                }
                setOpenConfirmDeleteModal(false);
            },
            onError: () => {
                toast.error(messageToast.messageToastTryCatch);
                setOpenConfirmDeleteModal(false);
            }
        });
    };

    return (
        <div className="p-0">
            <div className="p-3 bg-white border-bottom">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <span className="small text-muted mr-2">Hiển thị:</span>
                        <select
                            className="form-control form-control-sm"
                            value={pageSize}
                            onChange={(e) => setPageSize(e.target.value)}
                            style={{ width: "70px" }}
                        >
                            {objectShowNumber.map((item, key) => (
                                <option key={key} value={item.value}>{item.value}</option>
                            ))}
                        </select>
                    </div>
                    <Button 
                        variant="info" 
                        size="sm" 
                        onClick={handleOpenAdd}
                        className="shadow-sm px-3"
                    >
                        <i className="fas fa-plus mr-1" /> Thêm mới
                    </Button>
                </div>
            </div>

            <div className="table-responsive border-bottom">
                <Table striped bordered hover size="sm" className="mb-0 mobile-card-view">
                    <thead className="bg-light">
                        <tr>
                            <th className="text-center py-2" style={{ width: '60px' }}>STT</th>
                            <th className="py-2" style={{ width: '150px' }}>Mã</th>
                            <th className="py-2">Tên {title}</th>
                            <th className="py-2">Mô tả</th>
                            <th className="text-center py-2" style={{ width: '120px' }}>Thao tác</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan="5" className="text-center p-4">Đang tải...</td></tr>
                        ) : data?.data?.length > 0 ? (
                            data.data.map((item, index) => (
                                <tr key={item.id}>
                                    <td className="text-center align-middle" data-label="STT">
                                        {(page - 1) * pageSize + index + 1}
                                    </td>
                                    <td className="align-middle fw-bold" data-label="Mã">
                                        {item[codeField]}
                                    </td>
                                    <td className="align-middle" data-label="Tên">
                                        {item[nameField]}
                                    </td>
                                    <td className="align-middle" data-label="Mô tả">
                                        {item.description}
                                    </td>
                                    <td className="text-center align-middle py-2" data-label="Thao tác">
                                        <div className="d-flex justify-content-center gap-2">
                                            <button 
                                                className="btn btn-sm btn-outline-primary mr-1" 
                                                onClick={() => handleOpenEdit(item)}
                                                title="Sửa"
                                            >
                                                <i className="fas fa-edit" />
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-outline-danger" 
                                                onClick={() => handleDelete(item.id, item[nameField])}
                                                title="Xóa"
                                            >
                                                <i className="fas fa-trash" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan="5" className="text-center p-4">Không có dữ liệu</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <div className="d-flex justify-content-center py-3 bg-light">
                <Pagination
                    activePage={page}
                    itemsCountPerPage={Number(pageSize)}
                    totalItemsCount={data?.totalRecords || 0}
                    pageRangeDisplayed={5}
                    onChange={(p) => setPage(p)}
                    itemClass="page-item"
                    linkClass="page-link"
                    innerClass="pagination pagination-sm mb-0"
                />
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEdit ? 'Cập nhật' : 'Thêm mới'} {title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Mã <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                placeholder="Nhập mã..."
                            />
                        </Form.Group>
                        <Form.Group className="mt-3">
                            <Form.Label>Tên <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Nhập tên..."
                            />
                        </Form.Group>
                        <Form.Group className="mt-3">
                            <Form.Label>Mô tả</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Nhập mô tả..."
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Hủy</Button>
                    <Button variant="primary" onClick={handleSave}>Lưu lại</Button>
                </Modal.Footer>
            </Modal>

            <ConfirmModal
                isOpen={openConfirmDeleteModal}
                confirmType="delete"
                handleAccept={acceptDelete}
                handleCancel={() => setOpenConfirmDeleteModal(false)}
                content={confirmDelMessage}
            />
        </div>
    );
};

export default CategoryManager;
